import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ContentStatusBadge } from '@tms/shared-ui';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  activateInitialConfig,
  createBadge,
  createInitialConfig,
  deleteBadge,
  deleteInitialConfig,
  getBadges,
  getChallenges,
  getInitialProgressionConfigs,
  getProgressionLevels,
  updateBadge,
  updateInitialConfig,
  type Badge,
  type InitialConfig,
  type LevelRequirement,
} from '../services/progressionAdminApi';
import { BadgeIconField, isDefaultBadgeIcon } from '../components/BadgeIconField';
import { ClickableBadgeIcon } from '../components/BadgeIconLightbox';
import { ChallengesAdminTab } from '../components/ChallengesAdminTab';
import { LevelAdminTab } from '../components/LevelAdminTab';
import {
  ProgressionLocaleFields,
  emptyProgressionLocales,
  getProgressionDisplayName,
  progressionLocalesFromEntity,
  progressionPayloadFromLocales,
  type ProgressionLocalesForm,
} from '../components/ProgressionLocaleFields';
import { ContentPageLayout } from '../components/Shared';

const BADGE_CATEGORIES = ['document_group', 'domain', 'level', 'streak', 'milestone', 'special'];
const BADGE_RARITIES = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
const DOC_DOMAINS = ['ST', 'ACC', 'MD', 'M', 'SONG', 'EX', 'Other'];

type DocRow = { documentCode: string; domain: string; initialProgression: number; order: number };

const emptyDoc = (): DocRow => ({ documentCode: '', domain: 'ST', initialProgression: 2, order: 0 });

const ProgressionAdminPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [challengeCount, setChallengeCount] = useState(0);
  const [levels, setLevels] = useState<LevelRequirement[]>([]);
  const [configs, setConfigs] = useState<InitialConfig[]>([]);

  const [badgeDialog, setBadgeDialog] = useState<{ open: boolean; editing?: Badge }>({ open: false });
  const [badgeForm, setBadgeForm] = useState({
    locales: emptyProgressionLocales(),
    icon: '',
    category: 'document_group',
    rarity: 'common',
  });

  const [configDialog, setConfigDialog] = useState<{ open: boolean; editing?: InitialConfig }>({ open: false });
  const [configForm, setConfigForm] = useState({
    level: '1',
    name: '',
    description: '',
    isActive: true,
    documents: [emptyDoc()] as DocRow[],
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, ch, l, c] = await Promise.all([
        getBadges(),
        getChallenges(),
        getProgressionLevels(),
        getInitialProgressionConfigs(),
      ]);
      setBadges(b.data?.badges || []);
      setChallengeCount(ch.data?.challenges?.length ?? 0);
      setLevels(l.data?.levels || []);
      setConfigs(c.data?.configs || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load progression data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaveBadge = async () => {
    try {
      const body = {
        ...progressionPayloadFromLocales(badgeForm.locales),
        icon: badgeForm.icon,
        category: badgeForm.category,
        rarity: badgeForm.rarity,
      };
      if (badgeDialog.editing) {
        await updateBadge(badgeDialog.editing._id, body);
        setMsg('Badge updated.');
      } else {
        await createBadge(body);
        setMsg('Badge created.');
      }
      setBadgeDialog({ open: false });
      await load();
    } catch (e: any) {
      setMsg(e?.message || 'Badge save failed');
    }
  };

  const handleSaveConfig = async () => {
    try {
      const docs = configForm.documents.filter((d) => d.documentCode.trim());
      const body = {
        level: parseInt(configForm.level, 10),
        name: configForm.name,
        description: configForm.description,
        isActive: configForm.isActive,
        documents: docs,
      };
      if (configDialog.editing) {
        await updateInitialConfig(configDialog.editing._id, body);
        setMsg('Config updated.');
      } else {
        await createInitialConfig(body);
        setMsg('Config created.');
      }
      setConfigDialog({ open: false });
      await load();
    } catch (e: any) {
      setMsg(e?.message || 'Config save failed');
    }
  };

  const openBadgeEdit = (b?: Badge) => {
    setBadgeForm(
      b
        ? {
            locales: progressionLocalesFromEntity(b),
            icon: b.icon || '',
            category: b.category || 'document_group',
            rarity: b.rarity || 'common',
          }
        : { locales: emptyProgressionLocales(), icon: '', category: 'document_group', rarity: 'common' }
    );
    setBadgeDialog({ open: true, editing: b });
  };

  const openConfigEdit = (c?: InitialConfig) => {
    setConfigForm(
      c
        ? {
            level: String(c.level),
            name: c.name || '',
            description: c.description || '',
            isActive: c.isActive !== false,
            documents: (c.documents?.length ? c.documents : [emptyDoc()]).map((d) => ({
              documentCode: d.documentCode || '',
              domain: d.domain || 'ST',
              initialProgression: d.initialProgression ?? 2,
              order: d.order ?? 0,
            })),
          }
        : { level: '1', name: '', description: '', isActive: true, documents: [emptyDoc()] }
    );
    setConfigDialog({ open: true, editing: c });
  };

  if (loading) {
    return (
      <ContentPageLayout>
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
      </ContentPageLayout>
    );
  }

  return (
    <ContentPageLayout>
    <Box>
      <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
        Progression admin
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Manage badges, challenges, level requirements, and initial configs. Use user detail → Progression to initialize a user.
      </Typography>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {msg ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg}</Alert> : null}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Badges (${badges.length})`} />
        <Tab label={`Challenges (${challengeCount})`} />
        <Tab label={`Levels (${levels.length})`} />
        <Tab label={`Initial configs (${configs.length})`} />
      </Tabs>

      {tab === 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <Button size="small" startIcon={<AddIcon />} onClick={() => openBadgeEdit()}>Add badge</Button>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{'color':'primary.main'}}>Icon</TableCell>
                <TableCell sx={{'color':'primary.main'}}>Name</TableCell>
                <TableCell sx={{'color':'primary.main'}}>Category</TableCell>
                <TableCell sx={{'color':'primary.main'}}>Rarity</TableCell>
                <TableCell align="right" sx={{'color':'primary.main'}}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {badges.map((b) => (
                <TableRow key={b._id}>
                  <TableCell>
                    <ClickableBadgeIcon
                      icon={b.icon}
                      title={getProgressionDisplayName(b, 'en')}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {getProgressionDisplayName(b, 'en')}
                    </Typography>
                    {b.locales?.fr?.name ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        FR: {b.locales.fr.name}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{b.category || '—'}</TableCell>
                  <TableCell>{b.rarity || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openBadgeEdit(b)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={async () => { if (window.confirm('Delete badge?')) { await deleteBadge(b._id); await load(); } }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 1 && <ChallengesAdminTab onMessage={setMsg} onListChange={setChallengeCount} />}

      {tab === 2 && <LevelAdminTab onMessage={setMsg} />}

      {tab === 3 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <Button size="small" startIcon={<AddIcon />} onClick={() => openConfigEdit()}>Add config</Button>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{'color':'primary.main'}}>Level</TableCell>
                <TableCell sx={{'color':'primary.main'}}>Name</TableCell>
                <TableCell sx={{'color':'primary.main'}}>Active</TableCell>
                <TableCell sx={{'color':'primary.main'}}>Documents</TableCell>
                <TableCell align="right" sx={{'color':'primary.main'}}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((c) => (
                <TableRow key={c._id}>
                  <TableCell>{c.level}</TableCell>
                  <TableCell>{c.name || '—'}</TableCell>
                  <TableCell>{c.isActive ? <ContentStatusBadge status="active" /> : '—'}</TableCell>
                  <TableCell>{c.documents?.length ?? 0}</TableCell>
                  <TableCell align="right">
                    {!c.isActive ? (
                      <IconButton size="small" title="Activate" onClick={async () => { await activateInitialConfig(c._id); setMsg('Config activated.'); await load(); }}>
                        <CheckCircleIcon fontSize="small" color="primary" />
                      </IconButton>
                    ) : null}
                    <IconButton size="small" onClick={() => openConfigEdit(c)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={async () => { if (window.confirm('Delete config?')) { await deleteInitialConfig(c._id); await load(); } }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={badgeDialog.open} onClose={() => setBadgeDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{badgeDialog.editing ? 'Edit badge' : 'Create badge'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <ProgressionLocaleFields
              locales={badgeForm.locales}
              onChange={(locales: ProgressionLocalesForm) => setBadgeForm((f) => ({ ...f, locales }))}
            />
            <BadgeIconField
              icon={badgeForm.icon}
              onChange={(icon) => setBadgeForm((f) => ({ ...f, icon: isDefaultBadgeIcon(icon) ? '' : icon }))}
            />
            <TextField select label="Category" value={badgeForm.category} onChange={(e) => setBadgeForm((f) => ({ ...f, category: e.target.value }))}>
              {BADGE_CATEGORIES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
            <TextField select label="Rarity" value={badgeForm.rarity} onChange={(e) => setBadgeForm((f) => ({ ...f, rarity: e.target.value }))}>
              {BADGE_RARITIES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBadgeDialog({ open: false })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveBadge} disabled={!badgeForm.locales.en.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={configDialog.open} onClose={() => setConfigDialog({ open: false })} maxWidth="md" fullWidth>
        <DialogTitle>{configDialog.editing ? 'Edit initial config' : 'Create initial config'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Level" type="number" value={configForm.level} onChange={(e) => setConfigForm((f) => ({ ...f, level: e.target.value }))} />
            <TextField label="Name" value={configForm.name} onChange={(e) => setConfigForm((f) => ({ ...f, name: e.target.value }))} />
            <TextField label="Description" multiline value={configForm.description} onChange={(e) => setConfigForm((f) => ({ ...f, description: e.target.value }))} />
            <TextField select label="Active" value={configForm.isActive ? 'yes' : 'no'} onChange={(e) => setConfigForm((f) => ({ ...f, isActive: e.target.value === 'yes' }))}>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
            <Typography variant="subtitle2">Documents</Typography>
            {configForm.documents.map((d, idx) => (
              <Box key={idx} display="flex" gap={1} flexWrap="wrap" alignItems="center">
                <TextField size="small" label="Document code" value={d.documentCode} onChange={(e) => {
                  const docs = [...configForm.documents];
                  docs[idx] = { ...docs[idx], documentCode: e.target.value };
                  setConfigForm((f) => ({ ...f, documents: docs }));
                }} sx={{ flex: 2, minWidth: 180 }} />
                <TextField select size="small" label="Domain" value={d.domain} onChange={(e) => {
                  const docs = [...configForm.documents];
                  docs[idx] = { ...docs[idx], domain: e.target.value };
                  setConfigForm((f) => ({ ...f, documents: docs }));
                }} sx={{ minWidth: 100 }}>
                  {DOC_DOMAINS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Progress %" type="number" value={d.initialProgression} onChange={(e) => {
                  const docs = [...configForm.documents];
                  docs[idx] = { ...docs[idx], initialProgression: parseInt(e.target.value, 10) || 0 };
                  setConfigForm((f) => ({ ...f, documents: docs }));
                }} sx={{ width: 100 }} />
                <IconButton size="small" color="error" onClick={() => setConfigForm((f) => ({ ...f, documents: f.documents.filter((_, i) => i !== idx) }))}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button size="small" onClick={() => setConfigForm((f) => ({ ...f, documents: [...f.documents, emptyDoc()] }))}>Add document row</Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialog({ open: false })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveConfig}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ContentPageLayout>
  );
};

export default ProgressionAdminPage;
